import { useState, useEffect } from 'react';
import { Card, Row, Col, Tag, Skeleton, Segmented } from 'antd';
import { BankOutlined } from '@ant-design/icons';
import FinancialDashboardService from '../../utils/api/financial/FinancialDashboardService';
import './financial-dashboard.css';

const fmt = (value, currency = 'MXN') =>
  new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(value || 0);

const FIXED_ACCOUNTS = [
  { bank: 'BBVA',    currency_type: 'MXN' },
  { bank: 'BBVA',    currency_type: 'USD' },
  { bank: 'Banamex', currency_type: 'MXN' },
  { bank: 'Banamex', currency_type: 'USD' },
];

const PERSONA_OPTIONS = [
  { label: 'Total',         value: 'total'  },
  { label: 'Persona Moral', value: 'moral'  },
  { label: 'Persona Física',value: 'fisica' },
];

function BankCard({ bank, currency_type, amount, final_balance }) {
  const isMXN = currency_type === 'MXN';
  return (
    <div className="bank-card">
      <div className="bank-card-header">
        <BankOutlined className="bank-card-icon" />
        <span className="bank-card-name">{bank}</span>
        <Tag color={isMXN ? 'green' : 'geekblue'} style={{ marginLeft: 'auto' }}>
          {currency_type}
        </Tag>
      </div>
      <div className="bank-card-body">
        <div className="bank-card-stat">
          <span className="bank-stat-label">Monto Ingresado</span>
          <span className="bank-stat-value">{fmt(amount, currency_type)}</span>
        </div>
        <div className="bank-card-divider" />
        <div className="bank-card-stat">
          <span className="bank-stat-label">Saldo Final</span>
          <span className="bank-stat-value bank-stat-balance">{fmt(final_balance, currency_type)}</span>
        </div>
      </div>
    </div>
  );
}

export default function BankAccountIncome({ data = {}, loading, currentDateInfo, currentParams = {} }) {
  const [persona, setPersona] = useState('total');
  const [localData, setLocalData] = useState(null);
  const [localLoading, setLocalLoading] = useState(false);

  useEffect(() => {
    if (persona === 'total') {
      setLocalData(null);
      return;
    }

    let cancelled = false;
    setLocalLoading(true);

    FinancialDashboardService.getAccounts({ ...currentParams, person_type: persona })
      .then((res) => { if (!cancelled) setLocalData(res?.data || {}); })
      .catch(()  => { if (!cancelled) setLocalData({}); })
      .finally(() => { if (!cancelled) setLocalLoading(false); });

    return () => { cancelled = true; };
  }, [persona, currentParams]);

  const activeData = localData ?? data;
  const apiAccounts = activeData.data || [];

  // Siempre mostrar las 4 cuentas fijas; rellenar con datos del API si existen
  const accounts = FIXED_ACCOUNTS.map(({ bank, currency_type }) => {
    const found = apiAccounts.find(
      (a) =>
        a.bank?.toLowerCase() === bank.toLowerCase() &&
        a.currency_type === currency_type
    );
    return {
      bank,
      currency_type,
      amount:        found?.amount        || 0,
      final_balance: found?.final_balance || 0,
    };
  });

  const isLoading = loading || localLoading;

  return (
    <Card
      className="financial-card"
      title={
        <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
          <BankOutlined /> Ingreso Cuentas de Banco
        </span>
      }
      extra={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {currentDateInfo && (
            <span style={{ fontSize: '12px', color: '#888' }}>{currentDateInfo}</span>
          )}
          <Segmented
            size="small"
            options={PERSONA_OPTIONS}
            value={persona}
            onChange={setPersona}
          />
        </div>
      }
    >
      <Skeleton loading={isLoading} active paragraph={{ rows: 3 }}>
        <Row gutter={[16, 16]}>
          {accounts.map((acc, idx) => (
            <Col key={idx} xs={24} sm={12} xl={6}>
              <BankCard {...acc} />
            </Col>
          ))}
        </Row>
      </Skeleton>
    </Card>
  );
}
